import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useClientMutations } from './useClients';
import type { Client } from '@/types/database';

const schema = z.object({
  name: z.string().min(2, 'Nome é obrigatório'),
  responsible_name: z.string().optional(),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
  cnpj: z.string().optional(),
  cpf_responsavel: z.string().optional(),
  package: z.string().optional(),
  status: z.enum(['lead', 'onboarding', 'ativo', 'suspenso', 'cancelado']),
  contract_start: z.string().optional(),
  contract_end: z.string().optional(),
  auto_approval_limit: z.string().optional(),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

const PACKAGES: { value: string; label: string }[] = [
  { value: 'essencial', label: 'Essencial' },
  { value: 'avancado', label: 'Avançado' },
  { value: 'premium', label: 'Premium' },
];
const STATUS_LABELS: Record<Client['status'], string> = {
  lead: 'Lead',
  onboarding: 'Onboarding',
  ativo: 'Ativo',
  suspenso: 'Suspenso',
  cancelado: 'Cancelado',
};

function emptyToNull(v: string | undefined): string | null {
  return v?.trim() || null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  client?: Client | null;
}

export function ClientFormModal({ open, onClose, client }: Props) {
  const { create, update } = useClientMutations();
  const isEdit = Boolean(client);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { status: 'lead' },
  });

  useEffect(() => {
    if (client) {
      reset({
        name: client.name,
        responsible_name: client.responsible_name ?? '',
        email: client.email ?? '',
        phone: client.phone ?? '',
        cnpj: client.cnpj ?? '',
        cpf_responsavel: client.cpf_responsavel ?? '',
        package: client.package ?? '',
        status: client.status,
        contract_start: client.contract_start ?? '',
        contract_end: client.contract_end ?? '',
        auto_approval_limit: String(client.auto_approval_limit ?? 0),
        notes: client.notes ?? '',
      });
    } else {
      reset({ status: 'lead' });
    }
  }, [client, reset]);

  const onSubmit = async (data: FormData) => {
    const payload = {
      name: data.name,
      responsible_name: emptyToNull(data.responsible_name),
      email: emptyToNull(data.email),
      phone: emptyToNull(data.phone),
      cnpj: emptyToNull(data.cnpj),
      cpf_responsavel: emptyToNull(data.cpf_responsavel),
      package: emptyToNull(data.package),
      status: data.status,
      contract_start: emptyToNull(data.contract_start),
      contract_end: emptyToNull(data.contract_end),
      auto_approval_limit: parseFloat((data.auto_approval_limit ?? '0').replace(',', '.')) || 0,
      notes: emptyToNull(data.notes),
    };

    if (isEdit && client) {
      await update.mutateAsync({ id: client.id, ...payload });
    } else {
      await create.mutateAsync(payload);
    }
    onClose();
  };

  const currentStatus = watch('status');
  const currentPackage = watch('package');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">
            {isEdit ? 'Editar cliente' : 'Novo cliente'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Nome */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="name">Nome da clínica *</Label>
              <Input id="name" placeholder="Clínica Sorriso" {...register('name')} />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>

            {/* Responsável */}
            <div className="space-y-1.5">
              <Label htmlFor="responsible_name">Responsável</Label>
              <Input
                id="responsible_name"
                placeholder="Dr. João Silva"
                {...register('responsible_name')}
              />
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                placeholder="contato@clinica.com.br"
                {...register('email')}
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
            </div>

            {/* Telefone */}
            <div className="space-y-1.5">
              <Label htmlFor="phone">Telefone</Label>
              <Input id="phone" placeholder="(11) 99999-9999" {...register('phone')} />
            </div>

            {/* CNPJ */}
            <div className="space-y-1.5">
              <Label htmlFor="cnpj">CNPJ</Label>
              <Input id="cnpj" placeholder="00.000.000/0001-00" {...register('cnpj')} />
            </div>

            {/* CPF Responsável */}
            <div className="space-y-1.5">
              <Label htmlFor="cpf_responsavel">CPF do responsável</Label>
              <Input
                id="cpf_responsavel"
                placeholder="000.000.000-00"
                {...register('cpf_responsavel')}
              />
            </div>

            {/* Pacote */}
            <div className="space-y-1.5">
              <Label>Pacote</Label>
              <Select
                value={currentPackage || '__none__'}
                onValueChange={(v) => setValue('package', v === '__none__' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o pacote" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sem pacote</SelectItem>
                  {PACKAGES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="space-y-1.5">
              <Label>Status *</Label>
              <Select
                value={currentStatus}
                onValueChange={(v) => setValue('status', v as FormData['status'])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(STATUS_LABELS) as Client['status'][]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Início do contrato */}
            <div className="space-y-1.5">
              <Label htmlFor="contract_start">Início do contrato</Label>
              <Input id="contract_start" type="date" {...register('contract_start')} />
            </div>

            {/* Fim do contrato */}
            <div className="space-y-1.5">
              <Label htmlFor="contract_end">Fim do contrato</Label>
              <Input id="contract_end" type="date" {...register('contract_end')} />
            </div>

            {/* Limite de aprovação automática */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="auto_approval_limit">Limite de aprovação automática (R$)</Label>
              <Input
                id="auto_approval_limit"
                type="number"
                min="0"
                step="0.01"
                placeholder="0,00"
                {...register('auto_approval_limit')}
              />
              <p className="text-xs text-muted-foreground">
                Pagamentos abaixo desse valor são executados sem precisar da aprovação do cliente no
                portal. Use 0 para exigir aprovação em todos os pagamentos marcados como "exige
                aprovação".
              </p>
            </div>

            {/* Observações */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="notes">Observações</Label>
              <Textarea
                id="notes"
                placeholder="Informações adicionais sobre o cliente..."
                rows={3}
                {...register('notes')}
              />
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Salvando…
                </>
              ) : isEdit ? (
                'Salvar alterações'
              ) : (
                'Criar cliente'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
